'use client'

import { isValidElement, useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

export type DataTableSortDirection = 'ascending' | 'descending'
export type DataTableSortValue = string | number

export interface DataTableColumn<Row, ColumnId extends string> {
  id: ColumnId
  label: string
  render: (row: Row) => ReactNode
  header?: ReactNode
  sortValue?: (row: Row) => DataTableSortValue | null
  /**
   * Render a sortable header and keep the delivered row order. A column that
   * declares it is never reordered here even if it also carries a `sortValue`:
   * the order belongs to whoever selected the population, and reordering the
   * rows one page happens to hold is a different answer wearing the same arrow.
   */
  serverSortable?: boolean
  /** Overrides the default locale comparison for this column only. */
  compare?: (left: DataTableSortValue, right: DataTableSortValue) => number
  /** A secondary value used to make equal primary sort keys deterministic. */
  sortTieBreaker?: (row: Row) => DataTableSortValue | null
  className?: string
  headerClassName?: string
}

export interface DataTableSort<ColumnId extends string> {
  column: ColumnId
  direction: DataTableSortDirection
}

interface Props<Row, ColumnId extends string> {
  caption: string
  columns: readonly DataTableColumn<Row, ColumnId>[]
  rows: readonly Row[]
  rowFilter?: (row: Row) => boolean
  rowKey: (row: Row) => string
  rowLabel?: (row: Row) => string
  onRowActivate?: (row: Row, event?: MouseEvent<HTMLTableRowElement>) => void
  selectedRowKey?: string | null
  /** Background for the row whose detail is open; the default keeps the orders shade. */
  selectedRowClassName?: string
  rowClassName?: (row: Row) => string | undefined
  loading: boolean
  error?: string | null
  emptyMessage: string
  loadingMessage: string
  defaultSort?: DataTableSort<ColumnId>
  sort?: DataTableSort<ColumnId> | null
  onSortChange?: (sort: DataTableSort<ColumnId>) => void
  className?: string
}

function compareValues(
  left: DataTableSortValue | null,
  right: DataTableSortValue | null,
  compare?: (left: DataTableSortValue, right: DataTableSortValue) => number,
): number {
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  if (compare) return compare(left, right)
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), 'ru', { numeric: true, sensitivity: 'base' })
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(
    'a[href],button,input,select,textarea,summary,[role="button"],[role="link"]',
  ))
}

export function normalizeTableSearch(value: string): string {
  return value.trim().toLocaleLowerCase('ru-RU').replace(/\s+/g, ' ')
}

export function matchesTableSearch(query: string, values: readonly (string | null | undefined)[]): boolean {
  const normalized = normalizeTableSearch(query)
  if (!normalized) return true
  return values.some((value) => value != null && normalizeTableSearch(value).includes(normalized))
}

function textContent(value: ReactNode): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(textContent).join('')
  if (isValidElement<{ children?: ReactNode }>(value)) return textContent(value.props.children)
  return ''
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function DataTable<Row, ColumnId extends string>({
  caption,
  columns,
  rows,
  rowFilter,
  rowKey,
  rowLabel,
  onRowActivate,
  selectedRowKey = null,
  selectedRowClassName = 'bg-bone',
  rowClassName,
  loading,
  error = null,
  emptyMessage,
  loadingMessage,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  className = '',
}: Props<Row, ColumnId>) {
  const [internalSort, setInternalSort] = useState<DataTableSort<ColumnId> | null>(defaultSort ?? null)
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handleChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches)
    mediaQuery.addEventListener?.('change', handleChange)
    return () => mediaQuery.removeEventListener?.('change', handleChange)
  }, [])
  const controlled = controlledSort !== undefined
  const sort: DataTableSort<ColumnId> | null = (controlled ? controlledSort : internalSort) ?? null
  const setSort = (next: (current: DataTableSort<ColumnId> | null) => DataTableSort<ColumnId>) => {
    const value = next(sort)
    if (controlled) onSortChange?.(value)
    else setInternalSort(value)
  }
  const sortedRows = useMemo(() => {
    const visibleRows = loading || !rowFilter ? [...rows] : rows.filter(rowFilter)
    if (loading || !sort) return visibleRows
    const column = columns.find((candidate) => candidate.id === sort.column)
    if (column?.serverSortable === true) return visibleRows
    const sortValue = column?.sortValue
    if (!sortValue) return visibleRows
    const direction = sort.direction === 'ascending' ? 1 : -1
    return visibleRows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const leftValue = sortValue(left.row)
        const rightValue = sortValue(right.row)
        const leftUnavailable = leftValue === null
        const rightUnavailable = rightValue === null
        const primary = compareValues(leftValue, rightValue, column?.compare)
        if (primary !== 0) return leftUnavailable || rightUnavailable ? primary : primary * direction
        if (column.sortTieBreaker) {
          const leftTie = column.sortTieBreaker(left.row)
          const rightTie = column.sortTieBreaker(right.row)
          const tie = compareValues(leftTie, rightTie, column.compare)
          if (tie !== 0) {
            return leftTie === null || rightTie === null ? tie : tie * direction
          }
        }
        return left.index - right.index
      })
      .map(({ row }) => row)
  }, [columns, loading, rowFilter, rows, sort])

  const activateByKeyboard = (event: KeyboardEvent<HTMLTableRowElement>, row: Row) => {
    if (!onRowActivate || isInteractiveTarget(event.target)) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.currentTarget.focus()
    onRowActivate(row)
  }

  return (
    <div className={`min-w-0 overflow-x-auto rounded-lg border border-rule bg-paper ${className}`}>
      <table aria-busy={loading ? true : undefined} className="w-full min-w-[760px] border-collapse text-left text-xs">
        <caption className="sr-only">{caption}</caption>
        <thead className="border-b border-rule bg-bone/60 text-[10px] uppercase tracking-[0.08em] text-ink-muted">
          <tr>
            {columns.map((column) => {
              const sortable = column.serverSortable === true || Boolean(column.sortValue)
              const active = sort?.column === column.id
              const activeDirection = active ? sort?.direction : undefined
              const ariaSort = activeDirection ?? 'none'
              return (
                <th key={column.id} scope="col" aria-sort={sortable ? ariaSort : undefined} className={`px-3 py-3 font-semibold ${column.headerClassName ?? ''}`}>
                  {column.header ?? (sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-sm text-left hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep"
                      onClick={() => setSort((current) => ({
                        column: column.id,
                        direction: current?.column === column.id && current.direction === 'ascending'
                          ? 'descending'
                          : 'ascending',
                      }))}
                    >
                      {column.label}<span aria-hidden="true">{activeDirection ? (activeDirection === 'ascending' ? '↑' : '↓') : '↕'}</span>
                    </button>
                  ) : column.label)}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className={`divide-y divide-rule/70 ${loading && rows.length > 0 ? 'pointer-events-none opacity-60' : ''} ${loading && rows.length > 0 && !reducedMotion ? 'transition-opacity duration-150' : ''}`}>
          {error ? (
            <tr><td colSpan={columns.length} role="alert" className="px-4 py-10 text-center text-sm text-st-unpaid">{error}</td></tr>
          ) : loading && rows.length === 0 ? (
            <tr><td colSpan={columns.length} aria-live="polite" className="px-4 py-10 text-center text-sm text-ink-muted">{loadingMessage}</td></tr>
          ) : sortedRows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-ink-muted">{emptyMessage}</td></tr>
          ) : sortedRows.map((row) => {
            const key = rowKey(row)
            // While a search is in flight the shown rows are the previous
            // answer. Pointer events are already off; dropping the tab stop is
            // what keeps a keyboard from opening a row the list is replacing.
            const actionable = Boolean(onRowActivate) && !loading
            return (
              <tr
                key={key}
                tabIndex={actionable ? 0 : undefined}
                aria-label={actionable ? rowLabel?.(row) : undefined}
                aria-selected={selectedRowKey === key ? true : undefined}
                onClick={actionable ? (event) => {
                  if (isInteractiveTarget(event.target)) return
                  event.currentTarget.focus()
                  onRowActivate?.(row, event)
                } : undefined}
                onKeyDown={actionable ? (event) => activateByKeyboard(event, row) : undefined}
                className={actionable
                  ? `cursor-pointer outline-none transition-colors hover:bg-bone/45 focus-visible:bg-gold/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold-deep ${selectedRowKey === key ? selectedRowClassName : ''} ${rowClassName?.(row) ?? ''}`
                  : undefined}
              >
                {columns.map((column) => {
                  const content = column.render(row)
                  if (column.id !== 'title') {
                    return <td key={column.id} className={`px-3 py-3 align-middle ${column.className ?? ''}`}>{content}</td>
                  }
                  const fullTitle = textContent(content)
                  return <td key={column.id} className={`min-w-0 px-3 py-3 align-middle ${column.className ?? ''}`}>
                    <span className="block min-w-0 max-w-full truncate" title={fullTitle || undefined} aria-label={fullTitle || undefined}>{content}</span>
                  </td>
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {loading && rows.length > 0 ? <div aria-live="polite" className="border-t border-rule px-4 py-2 text-center text-[11px] text-ink-muted">{loadingMessage}</div> : null}
    </div>
  )
}
